import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import CubMascot, { AVATAR_OPTIONS } from '../components/CubMascot.jsx';
import { Alert, Toast, formatDate } from '../components/ui.jsx';
import TwoStepCard from '../components/TwoStepCard.jsx';
import PasswordRules from '../components/PasswordRules.jsx';

export default function Account() {
  const { user, refresh } = useAuth();
  const isStudent = user.role === 'student';

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [busy, setBusy] = useState(false);
  const [logins, setLogins] = useState([]);
  const [avatar, setAvatar] = useState(user.avatar);

  useEffect(() => {
    api.get('/auth/my-logins').then((d) => setLogins(d.logins)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next !== confirm) {
      setError('The two new passwords do not match. Try typing them again.');
      return;
    }
    setBusy(true);
    try {
      await api.changePassword(current, next);
      setCurrent('');
      setNext('');
      setConfirm('');
      setToast('Password changed! Remember the new one. 🔐');
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const pickAvatar = async (choice) => {
    setAvatar(choice);
    try {
      await api.post('/auth/avatar', { avatar: choice });
      await refresh();
      setToast('Your cub has a new look! 🎨');
    } catch (err) {
      setError(err.message);
      setAvatar(user.avatar);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{isStudent ? '🐻 My cub & my password' : '🔑 My account'}</h1>
          <p>
            Signed in as <strong>{user.userId}</strong>
            {user.schoolName ? ` · ${user.schoolName}` : ''}
          </p>
        </div>
      </div>

      {user.mustChangePassword && (
        <Alert kind="warn">
          <strong>Please pick your own password.</strong> Your account is still using the one your
          school gave you. Change it below so only you know it.
        </Alert>
      )}

      <div className="account-grid">
        {/* Superadmins only - see the card for why. */}
        {user.role === 'superadmin' && <TwoStepCard onChange={refresh} />}

        <section className="card">
          <h2>Change my password</h2>
          <p className="muted small" style={{ marginBottom: '1.2rem' }}>
            A good password is long and silly — three random words like{' '}
            <strong>PurpleGoatBanana</strong> are much stronger than one short tricky word.
          </p>

          {error && <Alert kind="bad" onDismiss={() => setError('')}>{error}</Alert>}

          <form onSubmit={submit}>
            <div className="field">
              <label htmlFor="cur">Current password</label>
              <input
                id="cur"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="new">New password</label>
              <input
                id="new"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                aria-describedby="new-password-rules"
              />
              <PasswordRules value={next} id="new-password-rules" />
            </div>
            <div className="field">
              <label htmlFor="conf">Type the new password again</label>
              <input
                id="conf"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                minLength={8}
                required
              />
            </div>
            <button type="submit" className="btn" disabled={busy}>
              {busy ? 'Saving…' : 'Save new password'}
            </button>
          </form>
        </section>

        <div className="stack gap-md">
          {isStudent && (
            <section className="card">
              <h2>Pick your cub</h2>
              <p className="muted small">This is the cub that shows up next to your name.</p>
              <div className="avatar-picker">
                {AVATAR_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`avatar-option ${avatar === opt ? 'is-picked' : ''}`}
                    onClick={() => pickAvatar(opt)}
                    aria-pressed={avatar === opt}
                    aria-label={`Choose the ${opt.replace('cub-', '')} cub`}
                  >
                    <CubMascot avatar={opt} size={64} mood="happy" />
                  </button>
                ))}
              </div>
            </section>
          )}

          <section className="card">
            <h2>Recent sign-ins</h2>
            <p className="muted small">
              If you see a sign-in that was not you, tell a teacher straight away.
            </p>
            {logins.length === 0 ? (
              <p className="faint">No sign-ins recorded yet.</p>
            ) : (
              <ul className="login-history">
                {logins.map((l, i) => (
                  <li key={i}>
                    <span>{formatDate(l.created_at, true)}</span>
                    <span className={l.success ? 'pill pill-good' : 'pill pill-bad'}>
                      {l.success ? 'Signed in' : 'Failed'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      <Toast message={toast} onDone={() => setToast('')} />
    </>
  );
}
